import { Suspense, useRef } from 'react';
import { useAtomValue } from 'jotai/react';
import type { GroupId, ViewModelId } from 'balus-common';
import {
  aiAssistantDrawerOpenAtom,
  aiAssistantDrawerWidthAtom,
  useAIAssistantDrawerResize,
} from './aiAssistantDrawerAtom';
import { AIAssistantContent } from './AIAssistantContent';
import { useIsAIAssistantEnabled } from './useIsAIAssistantEnabled';
import type { ViewModelPermissions } from '@view-model/application/context';

type Props = {
  viewModelId: ViewModelId;
  groupId: GroupId;
  viewModelPermissions: ViewModelPermissions;
};

export function AIAssistantDrawer({ viewModelId, groupId, viewModelPermissions }: Props) {
  const { enabled: isAIAssistantEnabled } = useIsAIAssistantEnabled(groupId);
  const isOpen = useAtomValue(aiAssistantDrawerOpenAtom);
  const width = useAtomValue(aiAssistantDrawerWidthAtom);
  const resizeHandlers = useAIAssistantDrawerResize();

  const hasOpenedRef = useRef(false);
  if (isOpen) hasOpenedRef.current = true;

  if (!isAIAssistantEnabled || !viewModelPermissions.isContentEditable) return null;
  if (!isOpen && !hasOpenedRef.current) return null;

  // 閉じている間も内部状態（チャット履歴の読み込み・スクロール位置・MCP ブリッジ等）を保持
  // するため、unmount せず display:none で隠す。
  return (
    <aside className={isOpen ? 'relative flex h-screen shrink-0 flex-col overflow-hidden' : 'hidden'} style={{ width }}>
      <Suspense fallback={null}>
        <AIAssistantContent key={viewModelId} viewModelId={viewModelId} groupId={groupId} />
      </Suspense>
      <div
        className="absolute left-0 top-0 h-full w-1 border-l-2 border-gray-300 hover:cursor-col-resize hover:border-gray-500"
        onPointerDown={resizeHandlers.onPointerDown}
        onPointerMove={resizeHandlers.onPointerMove}
        onPointerUp={resizeHandlers.onPointerUp}
        onPointerCancel={resizeHandlers.onPointerCancel}
        onLostPointerCapture={resizeHandlers.onLostPointerCapture}
      />
    </aside>
  );
}
